import { InputLabel, makeStyles, withStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { images } from 'utils/icons';
import Image from 'components/Image';
import Portal from 'portal/Portal';
import Table from 'components/Table';
import FormPopup from 'components/Operational/FormPopup';
import DetailedMediaSubform from './DetailedMediaSubForm';
import {
  generalSection,
  initialValues,
  multiUploadColumns,
  formLabels,
  formikFormKeys,
  mediaColumns,
} from './options';
import { PropTypes } from 'prop-types';
import ReactAudioPlayer from './ReactAudioPlayer';
import { MEDIA_TYPE_API_ENUMS } from '../../../../utils/constants/media';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import HighlightOffIcon from '@material-ui/icons/AddCircleOutlineSharp';
import CloseIcon from '@material-ui/icons/HighlightOff';
import { FORM } from 'utils/constants/css';
import IconWithText from 'components/IconWithText/iconWithText';

const WithColorAddIcon = withStyles(theme => ({
  root: {
    color: theme.palette.secondary.light,
  },
}))(HighlightOffIcon);

const useStyles = makeStyles(theme => ({
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '10px',
    alignItems: 'flex-start',
  },
  img: {
    padding: FORM.PADDING_BUTTON,
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    background: theme.palette.primary.main,
    justifyContent: 'flex-end',
    top: '0px',
    padding: '5px 20px',
  },
  cell: {
    width: '44%',
  },
  audioPlayer: {
    height: '400px',
    width: '400px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.9)',
  },
  disableContainer: {
    position: 'relative',
    pointerEvents: 'none',
    background: 'none',
    display: 'flex',
    flex: '0 0 2rem',
    height: '2rem',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '2rem',
    marginLeft: '3px',
  },
  hideContainer: {
    position: 'relative',
  },
  error: {
    color: '#f44336',
    marginTop: '0.5rem',
    marginLeft: '0.5rem',
    fontSize: '10px',
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export const MediaUploadForm = ({
  label = formikFormKeys.multiImageUploadButtonText,
  formProps, // formik props
  onChange, // sets the image in formik fields
  value = [], // this is an array of surfaces/audios
  fieldName,
  allowDeleteRow = true,
  allowEditRow = true,
  allowDelete = true,
  allowEdit = true,
  isMultipleImageRequired,
  error,
  tableColumns = mediaColumns,
  mediaHeader,
  mediaLabel,
  mediaType,
  disabled = false,
  noOfMediaFilesAllowed,
}) => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [displayPortal, setDisplayportal] = useState(null);
  const [popUpData, setPopUpData] = useState([]);
  const formFields = generalSection();
  const classes = useStyles();

  const closeForm = () => {
    setShowForm(false);
  };

  const toggleForm = () => {
    setPopUpData([]);
    setShowForm(!showForm);
  };

  const data =
    value.length > 0 &&
    value.map(media => ({
      // This structure allows us to re-use Table component
      allowDelete: allowDelete,
      allowEdit: allowEdit,
      rowData: {
        assetImage: {
          type:
            media?.mediaType === MEDIA_TYPE_API_ENUMS.AUDIO ? 'audio' : 'image',
          value: media?.url ? media.url : media.imageUrl,
        },
        name: media.name,
        description: media?.description ? media.description : '-',
      },
      rawData: {
        fileToBeDeleted: media.id,
        fileToOpenInPortal: media.id,
      },
    }));

  const onDelete = val => {
    const filteredImages = value.filter(f => f.id !== val.fileToBeDeleted);
    formProps.setFieldValue(
      fieldName,
      filteredImages.length > 0 ? filteredImages : [],
    );
  };

  const displayImageInPortal = mediaName => {
    const [mediaToDisplay] = value.filter(
      v => v.id === mediaName.fileToOpenInPortal,
    );
    setDisplayportal(mediaToDisplay);
  };

  const closePortal = () => {
    setDisplayportal(null);
  };

  const openEditForm = t => {
    const popupData = value.filter(v => v.id === t.fileToOpenInPortal);
    // get the index of this data
    const index = value.findIndex(f => f.id === t.fileToOpenInPortal);
    // add the index field of the data in the array
    const newPopupData = popupData.map((m, i) => {
      if (i === 0) {
        return { ...m, index };
      }
      return m;
    });
    setPopUpData(newPopupData);
    setShowForm(true);
  };

  const onSubmit = media => {
    const indexOfMedia = media[0].index;
    const nameOfMedia = media[0].name;
    const descriptionOfMedia = media[0].description;
    const urlOfMedia = media[0].url;
    const mediaType = media[0]?.mediaType;

    const newArray = [...value];

    if (indexOfMedia === undefined) {
      newArray.unshift(media[0]);
      onChange(newArray);
      return;
    }

    newArray[indexOfMedia] = {
      name: nameOfMedia,
      url: urlOfMedia,
      description: descriptionOfMedia,
      mediaType,
      id: media[0].id,
    };

    onChange(newArray);
  };
  const playAudio = audioSrc => {
    return (
      <div className={classes.audioPlayer}>
        <ReactAudioPlayer base64Audio={audioSrc.url} autoPlay={false} />
      </div>
    );
  };
  return (
    <>
      {allowDelete && (
        <div className={classes.labelContainer}>
          <InputLabel
            shrink
            required={isMultipleImageRequired}
            htmlFor="component-error"
            className={disabled ? classes.disabled : classes.input}
          >
            {noOfMediaFilesAllowed
              ? `${t(label)} - ${t('Maximum upload')}: ${noOfMediaFilesAllowed}`
              : t(label)}
            <span className={classes.error}>{error}</span>
          </InputLabel>
          <div
            className={cn(classes.hideContainer, {
              [classes.disableContainer]: disabled,
            })}
          >
            <Image
              text={`Add ${label}`}
              icon={<WithColorAddIcon />}
              title={label}
              src={images.addMedia.default}
              onClick={toggleForm}
              className={classes.img}
            />
          </div>
        </div>
      )}

      {showForm && (
        <FormPopup
          headerTitle={mediaHeader ? mediaHeader : formLabels.subFormHeader}
          closePopup={closeForm}
          form={
            <DetailedMediaSubform
              fields={formFields}
              defaultValues={initialValues}
              editValues={popUpData}
              cellClassName={classes.cell}
              cancel={closeForm}
              submit={media => {
                setShowForm(false);
                onSubmit(media);
              }}
              requiredFields={['surfaces']}
              label={mediaLabel}
              mediaType={mediaType}
              formProps={formProps}
              noOfMediaFilesAllowed={noOfMediaFilesAllowed}
            />
          }
        />
      )}

      {value && data && (
        <Table
          allowDeleteRow={allowDeleteRow}
          allowEditRow={allowEditRow}
          columns={tableColumns ? tableColumns : multiUploadColumns}
          data={data}
          onDelete={onDelete}
          showMediaInPortal={true}
          displayImageInPortal={displayImageInPortal}
          onEdit={t => openEditForm(t)}
        />
      )}
      {displayPortal && (
        <Portal id="portal" type="modal">
          <div className={classes.imageContainer}>
            <div className={classes.header}>
              <IconWithText
                onClick={closePortal}
                title={formLabels.title}
                theme={'light'}
                component={CloseIcon}
              />
            </div>

            {displayPortal.mediaType === MEDIA_TYPE_API_ENUMS.AUDIO ? (
              playAudio(displayPortal)
            ) : (
              <img
                src={
                  displayPortal?.url
                    ? displayPortal.url
                    : displayPortal.imageUrl
                }
                style={{ maxWidth: '100%' }}
                alt={'currentFileName'}
              />
            )}
          </div>
        </Portal>
      )}
    </>
  );
};

MediaUploadForm.propTypes = {
  onChange: PropTypes.func,
  formProps: PropTypes.object,
  value: PropTypes.array,
  label: PropTypes.string,
  error: PropTypes.bool,
  fieldName: PropTypes.string,
  allowDeleteRow: PropTypes.bool,
  allowEditRow: PropTypes.bool,
  allowDelete: PropTypes.bool,
  allowEdit: PropTypes.bool,
  id: PropTypes.string,
  displaySelectedImage: PropTypes.array,
  isMultipleImageRequired: PropTypes.bool,
  tableColumns: PropTypes.array,
  mediaHeader: PropTypes.string,
  mediaLabel: PropTypes.string,
  mediaType: PropTypes.array,
  disabled: PropTypes.bool,
  noOfMediaFilesAllowed: PropTypes.number,
};

MediaUploadForm.defaultProps = {
  mediaType: ['IMAGE'], // can be ['IMAGE','AUDIO']
};

export default MediaUploadForm;
